import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Video, Camera, X, RefreshCw, Square, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detect browser and device
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isMobile = /Mobi|Android/i.test(ua) || isIOS;
  
  return { isIOS, isSafari, isChrome, isFirefox, isAndroid, isMobile };
};

// Get best supported mime type
const getSupportedMimeType = () => {
  const { isIOS, isSafari } = getBrowserInfo();
  
  // iOS Safari prefers mp4
  if (isIOS || isSafari) {
    const iosTypes = [
      'video/mp4',
      'video/webm',
    ];
    for (const type of iosTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
  }
  
  // Other browsers - prefer webm
  const types = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus', 
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];
  
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  
  return ''; // Let browser choose default
};

export default function Step3Video({ data, onNext }) {
  const [skipVideo, setSkipVideo] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [consentVideo, setConsentVideo] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('user');
  const [recordingTime, setRecordingTime] = useState(0);
  const [browserInfo, setBrowserInfo] = useState({});
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const isRecordingRef = useRef(false);
  const recordingStartTime = useRef(null);

  useEffect(() => {
    setBrowserInfo(getBrowserInfo());
    checkPermissions();
    
    if (data) {
      setSkipVideo(data.skip_video || false);
      setConsentVideo(data.consent_video || false);
      if (data.video_url) setVideoUrl(data.video_url);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      stopEverything();
    };
  }, []);

  const checkPermissions = async () => {
    try {
      if (navigator.permissions) {
        const camera = await navigator.permissions.query({ name: 'camera' });
        const mic = await navigator.permissions.query({ name: 'microphone' });
        
        if (camera.state === 'denied' || mic.state === 'denied') {
          setPermissionStatus('denied');
        } else if (camera.state === 'granted' && mic.state === 'granted') {
          setPermissionStatus('granted');
        } else {
          setPermissionStatus('prompt');
        }
      }
    } catch (e) {
      // Permissions API not supported
      setPermissionStatus('unknown');
    }
  };

  const stopEverything = useCallback(() => {
    console.log('[Video] Stopping everything...');
    isRecordingRef.current = false;
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        console.warn('[Video] MediaRecorder stop error:', e);
      }
      mediaRecorderRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        try { 
          track.stop(); 
          console.log(`[Video] Stopped track: ${track.kind}`);
        } catch (e) {}
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    console.log('[Video] Starting camera...', { facingMode, browserInfo });
    setCameraError(null);
    setCameraReady(false);
    stopEverything();

    // Check for getUserMedia support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Votre navigateur ne supporte pas l\'accès à la caméra. Essayez Chrome, Safari ou Firefox.');
      return;
    }

    try {
      // Build constraints based on device capabilities
      const { isIOS, isMobile } = browserInfo;
      
      // Simpler constraints for better compatibility
      let constraints = {
        audio: true,
        video: {
          facingMode: facingMode
        }
      };

      // Add resolution constraints only for desktop
      if (!isMobile) {
        constraints.video = {
          ...constraints.video,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        };
      }

      // iOS specific handling
      if (isIOS) {
        constraints = {
          audio: true,
          video: {
            facingMode: { exact: facingMode }
          }
        };
      }

      console.log('[Video] Requesting media with constraints:', JSON.stringify(constraints));
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      const tracks = stream.getTracks();
      console.log('[Video] Got stream with tracks:', tracks.map(t => ({
        kind: t.kind,
        label: t.label,
        readyState: t.readyState,
        enabled: t.enabled
      })));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('autoplay', '');
        videoRef.current.setAttribute('playsinline', '');
        videoRef.current.setAttribute('muted', '');
        videoRef.current.muted = true;
        
        // Wait for video to be ready
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout: la caméra met trop de temps à démarrer'));
          }, 15000);
          
          const onCanPlay = () => {
            clearTimeout(timeout);
            videoRef.current.removeEventListener('canplay', onCanPlay);
            resolve();
          };
          
          videoRef.current.addEventListener('canplay', onCanPlay);
          
          // Try to play immediately
          videoRef.current.play().catch(e => {
            console.warn('[Video] Initial play failed, waiting for canplay event:', e);
          });
        });
        
        // Ensure video is playing
        if (videoRef.current.paused) {
          await videoRef.current.play();
        }
        
        setCameraReady(true);
        setPermissionStatus('granted');
        console.log('[Video] Camera ready!');
        toast.success('Caméra prête !');
      }
    } catch (error) {
      console.error('[Video] Camera error:', error.name, error.message);
      
      let msg = 'Impossible d\'accéder à la caméra.';
      
      switch (error.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
          msg = 'Accès caméra refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.';
          setPermissionStatus('denied');
          break;
        case 'NotFoundError':
        case 'DevicesNotFoundError':
          msg = 'Aucune caméra détectée sur cet appareil.';
          break;
        case 'NotReadableError':
        case 'TrackStartError':
          msg = 'La caméra est utilisée par une autre application. Fermez les autres apps et réessayez.';
          break;
        case 'OverconstrainedError':
          msg = 'La caméra ne supporte pas les paramètres demandés. Réessayez...';
          // Try with simpler constraints
          try {
            const simpleStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            streamRef.current = simpleStream;
            if (videoRef.current) {
              videoRef.current.srcObject = simpleStream;
              await videoRef.current.play();
              setCameraReady(true);
              toast.success('Caméra prête !');
              return;
            }
          } catch (e2) {
            console.error('[Video] Fallback also failed:', e2);
          }
          break;
        case 'AbortError':
          msg = 'L\'accès à la caméra a été interrompu. Réessayez.';
          break;
        case 'SecurityError':
          msg = 'Accès refusé pour des raisons de sécurité. Utilisez HTTPS.';
          break;
        default:
          msg = `Erreur caméra: ${error.message || error.name}`;
      }
      
      setCameraError(msg);
      toast.error(msg);
    }
  }, [facingMode, browserInfo, stopEverything]);

  const stopCamera = useCallback(() => {
    stopEverything();
    setCameraReady(false);
    setIsRecording(false);
    setRecordingTime(0);
  }, [stopEverything]);

  const switchCamera = useCallback(async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    console.log('[Video] Switching camera to:', newMode);
    setFacingMode(newMode);
    
    if (showCamera && !isRecording) {
      stopCamera();
      // Small delay before restarting
      setTimeout(() => {
        startCamera();
      }, 300);
    }
  }, [facingMode, showCamera, isRecording, startCamera, stopCamera]);

  const handleStartRecording = useCallback(() => {
    console.log('[Video] Starting recording...');
    
    if (!streamRef.current) {
      toast.error('Caméra non prête. Patientez...');
      startCamera();
      return;
    }

    // Verify tracks are live
    const tracks = streamRef.current.getTracks();
    const hasVideo = tracks.some(t => t.kind === 'video' && t.readyState === 'live');
    const hasAudio = tracks.some(t => t.kind === 'audio' && t.readyState === 'live');
    
    console.log('[Video] Track status - Video:', hasVideo, 'Audio:', hasAudio);
    
    if (!hasVideo) {
      toast.error('Flux vidéo perdu. Redémarrage de la caméra...');
      startCamera();
      return;
    }

    try {
      // Clear previous data
      chunksRef.current = [];
      
      // Get supported mime type
      const mimeType = getSupportedMimeType();
      console.log('[Video] Using mime type:', mimeType || 'browser default');
      
      // Create MediaRecorder
      const options = {};
      if (mimeType) {
        options.mimeType = mimeType;
      }
      
      // Add bitrate for better quality
      options.videoBitsPerSecond = 2500000; // 2.5 Mbps
      options.audioBitsPerSecond = 128000;  // 128 kbps
      
      let recorder;
      try {
        recorder = new MediaRecorder(streamRef.current, options);
      } catch (e) {
        console.warn('[Video] MediaRecorder with options failed, trying without:', e);
        recorder = new MediaRecorder(streamRef.current);
      }
      
      mediaRecorderRef.current = recorder;
      isRecordingRef.current = true;
      recordingStartTime.current = Date.now();

      recorder.ondataavailable = (event) => {
        console.log('[Video] Data chunk received:', event.data?.size || 0, 'bytes');
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstart = () => {
        console.log('[Video] Recording started');
        setIsRecording(true);
        setRecordingTime(0);
        
        timerRef.current = setInterval(() => {
          if (isRecordingRef.current) {
            setRecordingTime(prev => prev + 1);
          }
        }, 1000);
        
        toast.info('Enregistrement en cours...', { duration: 2000 });
      };

      recorder.onstop = () => {
        const duration = recordingStartTime.current ? (Date.now() - recordingStartTime.current) / 1000 : 0;
        console.log('[Video] Recording stopped. Duration:', duration, 's, Chunks:', chunksRef.current.length);
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        isRecordingRef.current = false;
        setIsRecording(false);
        
        // Process recorded data
        if (chunksRef.current.length > 0) {
          const totalSize = chunksRef.current.reduce((acc, chunk) => acc + chunk.size, 0);
          console.log('[Video] Total size:', totalSize, 'bytes');
          
          if (totalSize > 500 && duration > 0.5) {
            const blobType = mimeType || 'video/webm';
            const blob = new Blob(chunksRef.current, { type: blobType });
            const url = URL.createObjectURL(blob);
            
            console.log('[Video] Created blob:', blob.size, 'bytes, type:', blob.type);
            
            setVideoBlob(blob);
            setVideoUrl(url);
            setShowCamera(false);
            stopCamera();
            toast.success('Vidéo enregistrée !');
          } else {
            toast.error('Enregistrement trop court. Essayez de maintenir plus longtemps.');
          }
        } else {
          toast.error('Aucune donnée capturée. Réessayez.');
        }
      };

      recorder.onerror = (event) => {
        console.error('[Video] MediaRecorder error:', event.error);
        isRecordingRef.current = false;
        setIsRecording(false);
        toast.error('Erreur d\'enregistrement: ' + (event.error?.message || 'Inconnu'));
      };

      // Start recording with timeslice for continuous data
      // iOS Safari needs larger timeslice
      const timeslice = browserInfo.isIOS ? 1000 : 200;
      recorder.start(timeslice);
      console.log('[Video] MediaRecorder started with timeslice:', timeslice);
      
    } catch (error) {
      console.error('[Video] Start recording error:', error);
      toast.error('Impossible de démarrer l\'enregistrement: ' + error.message);
    }
  }, [browserInfo, startCamera, stopCamera]);

  const handleStopRecording = useCallback(() => {
    console.log('[Video] Stopping recording...');
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    isRecordingRef.current = false;

    if (mediaRecorderRef.current) {
      try {
        // Request any pending data first
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
        }
      } catch (e) {
        console.warn('[Video] requestData error:', e);
      }
      
      // Small delay then stop
      setTimeout(() => {
        try {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          }
        } catch (e) {
          console.error('[Video] Stop error:', e);
          setIsRecording(false);
        }
      }, 200);
    } else {
      setIsRecording(false);
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const uploadVideo = async (blob) => {
    console.log('[Video] Uploading video...', blob.size, 'bytes');
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const response = await fetch(`${API_URL}/api/videos/upload-base64`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ video_data: reader.result }),
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Upload failed');
          }
          
          const result = await response.json();
          console.log('[Video] Upload success:', result);
          resolve(result);
        } catch (error) {
          console.error('[Video] Upload error:', error);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read video file'));
      };
      
      reader.readAsDataURL(blob);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (skipVideo) {
      toast.success('Étape vidéo passée');
      onNext({ skip_video: true, video_recorded: false, consent_video: false });
      return;
    }
    
    if (!videoUrl || !videoBlob) {
      toast.error('Enregistrez une vidéo ou cochez "Je ne souhaite pas faire de vidéo"');
      return;
    }
    
    if (!consentVideo) {
      toast.error('Acceptez les conditions d\'utilisation');
      return;
    }

    // Try to upload video
    let uploadedVideo = null;
    let videoBase64 = null;
    
    try {
      toast.info('Upload de la vidéo en cours...');
      uploadedVideo = await uploadVideo(videoBlob);
      toast.success('Vidéo uploadée avec succès !');
    } catch (error) {
      console.warn('[Video] Initial upload failed:', error);
      
      // Convert blob to base64 for later retry
      try {
        videoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(videoBlob);
        });
        toast.info('Vidéo sauvegardée - Elle sera envoyée à l\'étape finale');
      } catch (e) {
        console.error('[Video] Failed to convert to base64:', e);
        toast.warning('Problème avec la vidéo. Continuez, elle sera réessayée plus tard.');
      }
    }

    onNext({
      skip_video: false,
      video_recorded: true,
      video_url: uploadedVideo?.url || videoUrl,
      video_id: uploadedVideo?.video_id || null,
      video_blob_base64: videoBase64, // Pass base64 for retry at final submission
      consent_video: consentVideo,
      upload_pending: !uploadedVideo && videoBase64 ? true : false
    });
  };

  const resetVideo = () => {
    if (videoUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(videoUrl);
    }
    setVideoUrl(null);
    setVideoBlob(null);
    setConsentVideo(false);
    chunksRef.current = [];
    setShowCamera(true);
    setTimeout(() => startCamera(), 200);
  };

  const openCamera = () => {
    setShowCamera(true);
    startCamera();
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-2">
          <Video className="w-8 h-8 text-accent" strokeWidth={1.5} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Vidéo témoignage
        </h2>
        <p className="text-sm sm:text-base text-foreground/80">
          Enregistrez une courte vidéo de satisfaction (optionnel)
        </p>
      </div>

      {/* Permission Warning */}
      {permissionStatus === 'denied' && !skipVideo && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Accès caméra bloqué</p>
            <p className="text-sm text-red-700 mt-1">
              {browserInfo.isIOS ? (
                <>Allez dans Réglages {'>'} Safari {'>'} Caméra et autorisez l'accès.</>
              ) : browserInfo.isChrome ? (
                <>Cliquez sur l'icône 🔒 dans la barre d'adresse et autorisez la caméra.</>
              ) : (
                <>Vérifiez les paramètres de votre navigateur pour autoriser l'accès à la caméra.</>
              )}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Skip option */}
        <div className="bg-card border border-border rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="skip-video-checkbox"
              type="checkbox"
              checked={skipVideo}
              onChange={(e) => {
                setSkipVideo(e.target.checked);
                if (e.target.checked) {
                  stopCamera();
                  setShowCamera(false);
                  setVideoUrl(null);
                  setVideoBlob(null);
                }
              }}
              className="w-5 h-5 mt-1 rounded border-input text-accent focus:ring-accent"
            />
            <div>
              <span className="text-sm font-medium block">Je ne souhaite pas faire de vidéo</span>
              <span className="text-xs text-foreground/60">Passer à l'étape suivante</span>
            </div>
          </label>
        </div>

        {!skipVideo && (
          <>
            {/* Script */}
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
              <p className="text-sm font-medium mb-2">📝 Phrase à répéter :</p>
              <p className="text-sm text-foreground/80 italic">
                "Je suis pleinement satisfait(e) des services et du professionnalisme de l'entreprise"
              </p>
            </div>

            {/* Start button */}
            {!videoUrl && !showCamera && (
              <button
                type="button"
                data-testid="show-camera-btn"
                onClick={openCamera}
                className="w-full h-20 bg-accent text-accent-foreground rounded-lg flex items-center justify-center gap-4 hover:bg-accent/90 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Camera className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-lg">Enregistrer une vidéo</p>
                  <p className="text-sm opacity-80">Appuyez pour démarrer la caméra</p>
                </div>
              </button>
            )}

            {/* Camera View */}
            {showCamera && !videoUrl && (
              <div className="space-y-4">
                <div className="bg-black rounded-lg overflow-hidden relative aspect-video">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                  />
                  
                  {/* Loading overlay */}
                  {!cameraReady && !cameraError && (
                    <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/30 border-t-white mb-4"></div>
                      <p className="text-white text-lg">Démarrage caméra...</p>
                      <p className="text-white/60 text-sm mt-2">Autorisez l'accès si demandé</p>
                    </div>
                  )}

                  {/* Error overlay */}
                  {cameraError && (
                    <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center">
                      <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
                      <p className="text-white mb-2 font-medium">Problème de caméra</p>
                      <p className="text-red-300 text-sm mb-4">{cameraError}</p>
                      <button 
                        type="button" 
                        onClick={startCamera} 
                        className="px-6 py-3 bg-accent text-white rounded-lg font-medium"
                      >
                        Réessayer
                      </button>
                    </div>
                  )}

                  {/* Recording indicator */}
                  {isRecording && (
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full">
                      <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                      <span className="font-mono font-bold">{formatTime(recordingTime)}</span>
                    </div>
                  )}

                  {/* Switch camera button */}
                  {cameraReady && !isRecording && (
                    <button
                      type="button"
                      onClick={switchCamera}
                      className="absolute top-4 right-4 p-3 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors"
                      title="Changer de caméra"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Recording controls */}
                <div className="flex gap-3">
                  {!isRecording ? (
                    <>
                      <button
                        type="button"
                        data-testid="start-recording-btn"
                        onClick={handleStartRecording}
                        disabled={!cameraReady}
                        className="flex-1 h-14 bg-red-600 text-white text-lg font-semibold rounded-lg hover:bg-red-700 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                        {cameraReady ? 'Démarrer l\'enregistrement' : 'Chargement...'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { stopCamera(); setShowCamera(false); }}
                        className="h-14 px-5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                        title="Fermer"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      data-testid="stop-recording-btn"
                      onClick={handleStopRecording}
                      className="flex-1 h-14 bg-gray-800 text-white text-lg font-semibold rounded-lg hover:bg-gray-900 flex items-center justify-center gap-3 transition-colors"
                    >
                      <Square className="w-5 h-5 fill-white" />
                      Arrêter ({formatTime(recordingTime)})
                    </button>
                  )}
                </div>
                
                {/* Help text */}
                {cameraReady && !isRecording && (
                  <p className="text-center text-sm text-foreground/60">
                    Appuyez sur "Démarrer" puis parlez face à la caméra
                  </p>
                )}
              </div>
            )}

            {/* Video Preview */}
            {videoUrl && (
              <div className="space-y-4">
                <div className="bg-card border-2 border-green-500 rounded-lg overflow-hidden">
                  <video 
                    src={videoUrl} 
                    controls 
                    playsInline 
                    className="w-full"
                  />
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">Vidéo enregistrée avec succès !</p>
                    <p className="text-sm text-green-600">Vérifiez le résultat et acceptez les conditions</p>
                  </div>
                </div>

                <button 
                  type="button" 
                  onClick={resetVideo} 
                  className="w-full h-12 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                >
                  Refaire la vidéo
                </button>
              </div>
            )}

            {/* Consent checkbox */}
            {videoUrl && (
              <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
                <h3 className="font-semibold text-lg mb-4">Autorisation de diffusion</h3>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="consent-video-checkbox"
                    type="checkbox"
                    checked={consentVideo}
                    onChange={(e) => setConsentVideo(e.target.checked)}
                    className="w-5 h-5 mt-1 rounded border-input text-accent focus:ring-accent"
                  />
                  <span className="text-sm leading-relaxed">
                    J'autorise l'entreprise à utiliser mon image et témoignage à des fins commerciales, 
                    sur tous supports, pour une durée illimitée.
                  </span>
                </label>
              </div>
            )}
          </>
        )}

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
          <p className="font-medium mb-1">🎥 Information</p>
          <p className="text-foreground/80">
            La vidéo est facultative. Vous conservez vos droits RGPD.
          </p>
        </div>

        {/* Submit button */}
        <button
          data-testid="next-step-btn"
          type="submit"
          className="w-full h-14 bg-accent text-accent-foreground text-lg font-semibold rounded-lg hover:bg-accent/90 transition-colors"
        >
          Continuer
        </button>
      </form>
    </div>
  );
}
